import ErrorBoundary from '@/components/ErrorBoundary';
import { DefaultComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { getViewportVisibilityClass, withDebugMetadata } from 'lib/utils';
import { cn } from 'lib/utils/cn';
import { getChildrenCount } from 'lib/utils/getChildrenCount';
import { withBackgroundColor } from 'lib/utils/withBackgroundColor';
import { withColumnsSystem } from 'lib/utils/withGridSystem';
import { withTextAlign } from 'lib/utils/withTextAlign';
import { renderContentComponent } from 'modules/dynamic-components';
import { Fragment, ReactNode } from 'react';
import { Row, RowProps } from './Row';
import { RowButtonProps } from './Row.Button';
import { RowCaptionProps } from './Row.Caption';
import { RowFooterProps } from './Row.Footer';
import { RowHeadlineProps } from './Row.Headline';

export interface StandaloneRowProps extends StandaloneComponentProps {
  button?: Pick<RowButtonProps, 'content' | 'link'>;
  caption?: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  headline?: ReactNode;
  options?: RowProps & {
    $button?: RowButtonProps['options'];
    $caption?: RowCaptionProps;
    $footer?: RowFooterProps;
    $headline?: RowHeadlineProps;
  };
}

export const StandaloneRow: StandaloneComponent<StandaloneRowProps> = ({
  button,
  caption,
  content,
  footer,
  headline,
  options,
  ...props
}) => {
  const {
    $button: buttonProps,
    $caption: captionProps,
    $footer: footerProps,
    $headline: headlineProps,
    ...baseProps
  } = options ?? {};

  return (
    <ErrorBoundary>
      <Row {...baseProps} {...props}>
        {headline && <Row.Headline {...headlineProps}>{headline}</Row.Headline>}
        {caption && <Row.Caption {...captionProps}>{caption}</Row.Caption>}
        {content}
        {(footer || button) && (
          <Row.Footer {...footerProps}>
            {footer}
            {button && <Row.Button content={button.content} link={button.link} {...buttonProps} />}
          </Row.Footer>
        )}
      </Row>
    </ErrorBoundary>
  );
};

export const standalone = StandaloneRow;

const DefaultRow: DefaultComponent = ({ type, data, meta, descendants }) => {
  if (!getChildrenCount(descendants)) {
    return null;
  }

  const backgroundColor = withBackgroundColor(meta.backgroundColor, true);
  const columns = withColumnsSystem(meta.width);
  const viewportVisibilityClass = getViewportVisibilityClass({
    viewportVisibility: meta.viewportVisibility,
    hideViewport: meta.hideViewport,
    display: 'grid',
  });

  const content = descendants.map((child, index) => {
    return <Fragment key={index}>{renderContentComponent(child)}</Fragment>;
  });

  const contentIndex = meta.contentIndex;
  const textAlign = withTextAlign(meta.textAlignRowTitle);

  return (
    <StandaloneRow
      headline={data.title}
      content={content}
      button={data.rowButton}
      options={{
        className: cn(backgroundColor, columns, viewportVisibilityClass),
        size: backgroundColor ? 'alternative' : 'default',
        $headline: { className: cn(textAlign) },
      }}
      data-content-index={contentIndex}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export default DefaultRow;
